import { UseQueryResult, useQuery } from '@tanstack/react-query';

import { ListItemDto, MainSegment, useProductCenterApi } from 'api';
import { detailedSegmentQuery, editableDetailedSegments } from 'api/queries';

// #1267899 - TODO: REMOVE AFTER PROD RELEASE
const isYachtFeatureOn = () => import.meta.env.VITE_IS_YACHT === 'true';

/**
 * React hook to fetch detailed segments for a given main segment.
 *
 * - Uses React Query to request segment metadata from the Product Center API.
 * - When the Yacht feature flag (VITE_IS_YACHT) is enabled and not in readonly mode,
 *   it uses the extended detailed segments query and may include provided product serial numbers.
 * - Otherwise, it falls back to the standard detailed segment query.
 *
 * Parameters
 * - mainSegment: Selected main segment; if null, the underlying query factory decides enablement.
 * - productIndividualSerialNumbers: Optional serial numbers used by the extended query variant.
 * - isReadonly: When true, forces the standard query (ignores Yacht extension). Default true.
 * - isEnabled: Overall enablement flag for the query; default true. When false, the query will not run.
 *
 * Returns
 * - The TanStack Query result for the request (status, data, error, etc.).
 */
export const useDetailedSegments = (
	mainSegment: MainSegment | null,
	productIndividualSerialNumbers: string[] = [],
	isReadonly: boolean = true,
	isEnabled: boolean = true
): UseQueryResult<ListItemDto[], unknown> => {
	const { api } = useProductCenterApi();
	const { data, ...rest } = useQuery({
		...(!isReadonly
			? editableDetailedSegments(
					api,
					mainSegment,
					productIndividualSerialNumbers
				)
			: detailedSegmentQuery(api, mainSegment)),
		enabled: isEnabled,
	});

	return {
		data: isYachtFeatureOn()
			? data
			: data?.filter(({ value }) => value !== 'Y'),
		...rest,
	} as UseQueryResult<ListItemDto[], unknown>;
};
